﻿// --- START OF FILE Header.jsx ---

import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Menu, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import vvaLogo from '../assets/vva-logo.png'; 

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navItems = [
    { name: 'Solutions', href: '#solutions' },
    { name: 'About', href: '#about' },
    { name: 'Contact', href: '#contact' },
  ];

  const handleNavClick = (href) => {
    document.querySelector(href)?.scrollIntoView({ behavior: 'smooth' });
    setIsMobileMenuOpen(false);
  };

  const handleGetStarted = () => handleNavClick('#contact');

  return (
    <motion.header
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? 'glass-effect shadow-xl py-2' : 'bg-transparent py-4'
      }`}
    >
      <nav className="container mx-auto px-4 md:px-8 flex items-center justify-between">
        
        {/* --- BRAND IDENTITY SECTION --- */}
        <motion.a
          href="#"
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          className="flex items-center gap-4 group cursor-pointer" // Increased gap for better centering
          onClick={() => handleNavClick('#')}
        >
          {/* Logo Image */}
          <img 
            src={vvaLogo} 
            alt="VegaVelox Emblem" 
            className="h-10 md:h-12 w-auto object-contain brand-logo-img" 
          />

          {/* Text Only - Vertically Centered with Logo */}
          <span className="brand-title gradient-text text-xl md:text-2xl font-bold tracking-wide whitespace-nowrap pt-1">
            VEGAVELOX ANALYTECH
          </span>
        </motion.a>

        {/* Desktop nav */}
        <div className="hidden md:flex items-center space-x-8">
          {navItems.map((item, index) => (
            <motion.button
              key={item.name}
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
              onClick={() => handleNavClick(item.href)}
              className="text-gray-300 hover:text-white transition-colors duration-200 font-medium"
            >
              {item.name}
            </motion.button>
          ))}
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.3 }}
          >
            <Button
              onClick={handleGetStarted}
              className="bg-[hsl(var(--primary-gold))] text-black px-6 py-2 rounded-lg font-bold transition-all duration-300 hover:brightness-110 shadow-lg hover:shadow-yellow-500/20"
            >
              Get Started
            </Button>
          </motion.div>
        </div>

        {/* Mobile menu button */}
        <div className="md:hidden">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="text-white"
          >
            {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </Button>
        </div>
      </nav>

      {/* Mobile menu */}
      {isMobileMenuOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="md:hidden mt-2 glass-effect rounded-lg p-4 mx-4"
        >
          {navItems.map((item) => (
            <button
              key={item.name}
              onClick={() => handleNavClick(item.href)}
              className="block w-full text-left py-3 text-gray-300 hover:text-white transition-colors duration-200"
            >
              {item.name}
            </button>
          ))}
          <Button
            onClick={handleGetStarted}
            className="w-full mt-4 bg-[hsl(var(--primary-gold))] text-black font-bold"
          >
            Get Started
          </Button>
        </motion.div>
      )}
    </motion.header>
  );
};

export default Header;
// --- END OF FILE Header.jsx ---